import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AgendaAgendaOpcao,
  AgendaAgendamentoCriado,
  AgendaAgendamentoPaciente,
  AgendaAgendasParams,
  AgendaAlertaTurno,
  AgendaBloqueio,
  AgendaCanalContato,
  AgendaCancelarRequest,
  AgendaClassificacao,
  AgendaClassificacaoRequest,
  AgendaColunaPref,
  AgendaContexto,
  AgendaConvenioRequest,
  AgendaDesvincularContaRequest,
  AgendaDisponibilidade,
  AgendaDisponibilidadeParams,
  AgendaDuplicidade,
  AgendaDuplicidadeParams,
  AgendaEncaixeRequest,
  AgendaEscrita,
  AgendaEspecialidadeAlterada,
  AgendaEspecialidadeDaAgenda,
  AgendaEspecialidadeRequest,
  AgendaEspecialidadesParams,
  AgendaEstabelecimento,
  AgendaEstabelecimentosParams,
  AgendaExame,
  AgendaGrade,
  AgendaGradeParams,
  AgendaMedico,
  AgendaMedicosParams,
  AgendaMesDia,
  AgendaMesParams,
  AgendaNovoAgendamentoRequest,
  AgendaObservacaoRequest,
  AgendaOpcao,
  AgendaPlanoPaciente,
  AgendaPrecoPaciente,
  AgendaPrecoParams,
  AgendaPrecos,
  AgendaPrecosParams,
  AgendaPreparoExame,
  AgendaProcedimentoAlterado,
  AgendaProcedimentoRequest,
  AgendaReservaRequest,
  AgendaResumoDia,
  AgendaResumoParams,
  AgendaRetornoDisponivel,
  AgendaRetornoItem,
  AgendaStatus,
  AgendaStatusAlterado,
  AgendaStatusRequest,
  AgendaTipoAtendimentoOpcao,
  AgendaTipoAtendimentoRequest,
  AgendaTipoConsultaRequest,
  AgendaTransferenciaLote,
  AgendaTransferida,
  AgendaTransferirLoteRequest,
  AgendaTransferirRequest,
  AgendaTrocarPacienteRequest,
  AgendaTurnoParams,
  AgendaUf,
  AgendaVagas,
  AgendaVagasParams,
} from '../../interfaces/agenda';

type Parametros = Record<string, string | number | boolean | null | undefined>;

/**
 * Mensagem para mostrar ao operador a partir de um erro das rotas da Agenda. A regra recusada
 * vem em notifications (ApplicationException vira 400); o 403 de marca vem no feedback.
 */
export function mensagemErroAgenda(e: unknown, padrao = 'Não foi possível concluir a operação.'): string {
  if (e instanceof HttpErrorResponse) {
    const corpo = e.error;
    const notificacao = corpo?.notifications?.find((n: any) => n?.message)?.message;
    if (notificacao) return notificacao;
    if (typeof corpo?.feedback === 'string' && corpo.feedback) return corpo.feedback;
    if (e.status === 0) return 'Sem conexão com o servidor.';
    return padrao;
  }
  return e instanceof Error && e.message ? e.message : padrao;
}

/** Agenda (porte do sim-relatorios). O cabeçalho X-Unidade vem do UnidadeAdesaoInterceptor. */
@Injectable({ providedIn: 'root' })
export class AgendaService {
  private apiURL = `${environment.apiUrl}/v${environment.apiVersion}/Agenda`;

  constructor(private http: HttpClient) {}

  // --- Contexto e filtros ---------------------------------------------------------------------

  contexto(): Promise<AgendaContexto | null> {
    return this.item('contexto');
  }

  estabelecimentos(p: AgendaEstabelecimentosParams = {}): Promise<AgendaEstabelecimento[]> {
    return this.lista('filtros/estabelecimentos', { ...p });
  }

  especialidades(p: AgendaEspecialidadesParams): Promise<AgendaOpcao[]> {
    return this.lista('filtros/especialidades', { ...p });
  }

  agrupamentos(cdEstabelecimento: number | null): Promise<AgendaOpcao[]> {
    return this.lista('filtros/agrupamentos', { cdEstabelecimento });
  }

  medicos(p: AgendaMedicosParams): Promise<AgendaMedico[]> {
    return this.lista('filtros/medicos', { ...p });
  }

  agendas(p: AgendaAgendasParams): Promise<AgendaAgendaOpcao[]> {
    return this.lista('filtros/agendas', { ...p });
  }

  classificacoes(): Promise<AgendaClassificacao[]> {
    return this.lista('filtros/classificacoes');
  }

  status(): Promise<AgendaStatus[]> {
    return this.lista('filtros/status');
  }

  canaisContato(): Promise<AgendaCanalContato[]> {
    return this.lista('filtros/canais-contato');
  }

  tiposAtendimento(): Promise<AgendaTipoAtendimentoOpcao[]> {
    return this.lista('filtros/tipos-atendimento');
  }

  bloqueios(cdAgenda: number, data: string): Promise<AgendaBloqueio[]> {
    return this.lista('filtros/bloqueios', { cdAgenda, data });
  }

  turnoEncerramento(p: AgendaTurnoParams = {}): Promise<AgendaAlertaTurno[]> {
    return this.lista('filtros/turno-encerramento', { ...p });
  }

  /** Com cdAgenda, na Super, a lista segue a regra do médico titular da agenda. */
  convenios(cdEstabelecimento: number | null, cdAgenda: number | null = null): Promise<AgendaOpcao[]> {
    return this.lista('filtros/convenios', { cdEstabelecimento, cdAgenda });
  }

  categorias(cdConvenio: number | null, cdAgenda: number | null = null): Promise<AgendaOpcao[]> {
    return this.lista('filtros/categorias', { cdConvenio, cdAgenda });
  }

  especialidadesDaAgenda(cdAgenda: number): Promise<AgendaEspecialidadeDaAgenda[]> {
    return this.lista('especialidades-da-agenda', { cdAgenda });
  }

  // --- Grade, resumo e mês --------------------------------------------------------------------

  async grade(p: AgendaGradeParams): Promise<AgendaGrade> {
    const g = await this.item<AgendaGrade>('grade', { ...p });
    return {
      linhas: g?.linhas ?? [],
      paginacao: g?.paginacao ?? { page: 1, limit: p.limit ?? 50, total: 0 },
      resumo: g?.resumo
        ? { ...g.resumo, encaixes: g.resumo.encaixes ?? 0 }
        : { horariosDia: 0, horariosLivres: 0, horariosOcupados: 0, horariosRetorno: null, limiteRetornos: null, encaixes: 0 },
    };
  }

  resumo(p: AgendaResumoParams): Promise<AgendaResumoDia | null> {
    return this.item('grade/resumo', { ...p });
  }

  mes(p: AgendaMesParams): Promise<AgendaMesDia[]> {
    return this.lista('mes', { ...p });
  }

  // --- Apoio ao agendamento -------------------------------------------------------------------

  exames(q: string, cdAgenda?: number | null): Promise<AgendaExame[]> {
    return this.lista('exames', { q, cdAgenda });
  }

  preparoExame(nrSeqProcInterno: number): Promise<AgendaPreparoExame | null> {
    return this.item('exames/preparo', { nrSeqProcInterno });
  }

  precoPaciente(p: AgendaPrecoParams): Promise<AgendaPrecoPaciente | null> {
    return this.item('preco-paciente', { ...p });
  }

  agendamentosDoPaciente(cdPessoaFisica: string | number, dtInicio?: string | null, dtFim?: string | null): Promise<AgendaAgendamentoPaciente[]> {
    return this.lista(`paciente/${encodeURIComponent(String(cdPessoaFisica))}/agendamentos`, { dtInicio, dtFim });
  }

  planoPaciente(cdPessoaFisica: string | number): Promise<AgendaPlanoPaciente | null> {
    return this.item(`paciente/${encodeURIComponent(String(cdPessoaFisica))}/plano`);
  }

  retornoDisponivel(cdAgenda: number, data: string, classif: string): Promise<AgendaRetornoDisponivel | null> {
    return this.item('retorno-disponivel', { cdAgenda, data, classif });
  }

  async retornoDisponivelLote(itens: AgendaRetornoItem[]): Promise<AgendaRetornoDisponivel[]> {
    const r = await this.escrever<AgendaRetornoDisponivel>('post', 'retorno-disponivel/lote', { itens });
    return r.lista;
  }

  duplicidade(p: AgendaDuplicidadeParams): Promise<AgendaDuplicidade | null> {
    return this.item('duplicidade', { ...p });
  }

  // --- Gravação -------------------------------------------------------------------------------

  criarAgendamento(req: AgendaNovoAgendamentoRequest): Promise<AgendaEscrita<AgendaAgendamentoCriado>> {
    return this.escrita('post', 'agendamentos', req);
  }

  criarEncaixe(req: AgendaEncaixeRequest): Promise<AgendaEscrita<AgendaAgendamentoCriado>> {
    return this.escrita('post', 'agendamentos/encaixe', req);
  }

  reservarHorario(req: AgendaReservaRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('post', 'horarios/reservar', req);
  }

  liberarReserva(req: AgendaReservaRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('post', 'horarios/liberar-reserva', req);
  }

  cancelar(nrSequencia: number, req: AgendaCancelarRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('post', `agendamentos/${nrSequencia}/cancelar`, req);
  }

  alterarStatus(nrSequencia: number, req: AgendaStatusRequest): Promise<AgendaEscrita<AgendaStatusAlterado>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/status`, req);
  }

  // --- Vagas e transferência ------------------------------------------------------------------

  async vagas(p: AgendaVagasParams): Promise<AgendaVagas> {
    const v = await this.item<AgendaVagas>('vagas', { ...p });
    return { unidades: v?.unidades ?? [], totalVagas: v?.totalVagas ?? 0 };
  }

  // --- Encontrar disponibilidade ------------------------------------------------------------

  async disponibilidade(p: AgendaDisponibilidadeParams): Promise<AgendaDisponibilidade> {
    const d = await this.item<AgendaDisponibilidade>('disponibilidade', { ...p });
    return {
      unidades: d?.unidades ?? [],
      datas: d?.datas ?? [],
      totalVagas: d?.totalVagas ?? 0,
      limitado: !!d?.limitado,
      retornosVerificados: !!d?.retornosVerificados,
      nrSeqProcInterno: d?.nrSeqProcInterno ?? null,
      cdProcedimento: d?.cdProcedimento ?? null,
      preparo: d?.preparo ?? null,
    };
  }

  ufs(tipo?: number | null, areaAtuacao?: number | null): Promise<AgendaUf[]> {
    return this.lista('filtros/ufs', { tipo, areaAtuacao });
  }

  /** Erro sobe: quem chama mostra "erro ao consultar", nunca "sem preço". */
  precos(p: AgendaPrecosParams): Promise<AgendaPrecos | null> {
    return this.item('precos', { ...p });
  }

  transferir(req: AgendaTransferirRequest): Promise<AgendaEscrita<AgendaTransferida>> {
    return this.escrita('post', 'agendamentos/transferir', req);
  }

  transferirLote(req: AgendaTransferirLoteRequest): Promise<AgendaEscrita<AgendaTransferenciaLote>> {
    return this.escrita('post', 'agendamentos/transferir-lote', req);
  }

  // --- Alterações do agendamento --------------------------------------------------------------

  alterarClassificacao(nrSequencia: number, req: AgendaClassificacaoRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/classificacao`, req);
  }

  alterarConvenio(nrSequencia: number, req: AgendaConvenioRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/convenio`, req);
  }

  alterarTipoAtendimento(nrSequencia: number, req: AgendaTipoAtendimentoRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/tipo-atendimento`, req);
  }

  alterarTipoConsulta(nrSequencia: number, req: AgendaTipoConsultaRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/tipo-consulta`, req);
  }

  alterarProcedimento(nrSequencia: number, req: AgendaProcedimentoRequest): Promise<AgendaEscrita<AgendaProcedimentoAlterado>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/procedimento`, req);
  }

  alterarEspecialidade(nrSequencia: number, req: AgendaEspecialidadeRequest): Promise<AgendaEscrita<AgendaEspecialidadeAlterada>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/especialidade`, req);
  }

  alterarObservacao(nrSequencia: number, req: AgendaObservacaoRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/observacao`, req);
  }

  trocarPaciente(nrSequencia: number, req: AgendaTrocarPacienteRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('put', `agendamentos/${nrSequencia}/paciente`, req);
  }

  desvincularConta(nrSequencia: number, req: AgendaDesvincularContaRequest): Promise<AgendaEscrita<unknown>> {
    return this.escrita('post', `agendamentos/${nrSequencia}/desvincular-conta`, req);
  }

  // --- Preferência de colunas (mesma linha do sim-relatorios) --------------------------------

  /** O array salvo cru; null = nada salvo. Quem chama mescla com o padrão. */
  async preferenciasColunas(): Promise<unknown> {
    const r = await this.item<{ columns?: unknown }>('preferencias/colunas');
    return r?.columns ?? null;
  }

  salvarPreferenciasColunas(columns: AgendaColunaPref[]): Promise<AgendaEscrita<unknown>> {
    return this.escrita('put', 'preferencias/colunas', { columns });
  }

  // --- Infra ----------------------------------------------------------------------------------

  /** Query sem os vazios: número vazio na query dá 400 no model binding do back. */
  static montarParams(p: Parametros = {}): HttpParams {
    let params = new HttpParams();
    for (const [chave, valor] of Object.entries(p)) {
      if (valor === null || valor === undefined || valor === '') continue;
      if (typeof valor === 'number' && !Number.isFinite(valor)) continue;
      params = params.set(chave, String(valor));
    }
    return params;
  }

  // Leitura vazia pode vir como 200 com data nula ou como 404 (SetFeedbackMessage); as duas são "nada".
  private async lista<T>(caminho: string, p?: Parametros): Promise<T[]> {
    try {
      const r = await firstValueFrom(
        this.http.get<any>(`${this.apiURL}/${caminho}`, { params: AgendaService.montarParams(p) })
      );
      return r?.data ?? [];
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 404) return [];
      throw e;
    }
  }

  private async item<T>(caminho: string, p?: Parametros): Promise<T | null> {
    const dados = await this.lista<T>(caminho, p);
    return dados[0] ?? null;
  }

  private async escrever<T>(metodo: 'post' | 'put', caminho: string, corpo: unknown): Promise<{ lista: T[]; mensagem: string | null }> {
    const url = `${this.apiURL}/${caminho}`;
    const r = await firstValueFrom(
      metodo === 'post' ? this.http.post<any>(url, corpo) : this.http.put<any>(url, corpo)
    );
    return { lista: r?.data ?? [], mensagem: r?.feedback ?? null };
  }

  private async escrita<T>(metodo: 'post' | 'put', caminho: string, corpo: unknown): Promise<AgendaEscrita<T>> {
    const r = await this.escrever<T>(metodo, caminho, corpo);
    return { dado: r.lista[0] ?? null, mensagem: r.mensagem };
  }
}
